import React, {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  camelCase,
  compact,
  difference,
  every,
  filter,
  find,
  groupBy,
  has,
  includes,
  isEmpty,
  isEqual,
  join,
  isUndefined,
  keys,
  map,
  omit,
  orderBy,
  reduce,
  reject,
  size,
  toLower,
  uniq,
  values,
} from 'lodash';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Step,
  StepLabel,
  Stepper,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Scrollspy } from '@makotot/ghostui';
import { Search } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

import { grey } from '@mui/material/colors';

import { enqueueSnackbar } from 'notistack';

import {
  formatParameters,
  IFormatParameters,
} from 'src/components/Visualization/formatParameters';

import { WEB_ASSUMPTIONS_POST, WEB_ASSUMPTIONS_LOAD } from 'src/url';

import { getDataAtIntervals } from 'src/utils/getDataAtIntervals';

import {
  Model,
  ModelInstance,
  RowIdentifier,
  Scenario,
  SubmissionStatus,
} from '../../../types/models';
import useSetState from '../../../hooks/useSetState';

import PopupDialog from '../../shared/PopupDialog';

import CenteredSpinner from '../../shared/CenteredSpinner';

import ClearableTextField from '../../shared/ClearableTextField';

import { useData } from '../../../hooks/useData';

import * as API from '../../../services/API';

import { GetParams } from '../../../utils/history';

import RemoveInputNamesDialog from './RemoveInputNamesDialog';
import FilterDimAndInstStep, {
  getCheckedNames,
  getEmptyDimensionNames,
} from './FilterDimAndInstStep';
import {
  exportInputsAsExcel,
  filterInputsBySelection,
  ITemplateScope,
} from './exportInputsAsExcel';
import {
  IUploadedTemplateOverrides,
  parseTemplateScope,
  parseTemplateWorkbook,
  sheetNameFor,
  templateScopeMatches,
} from './importInputsAsExcel';

import { IWebAssumptionsLoad } from './types';

import { IInputsResponse, ISelectedInputs } from './index';

interface ISelectInputNamesModalProps {
  isOpen: boolean;
  onClose: () => void;
  modelInstance?: ModelInstance;
  scenario?: Scenario;
  modelInstanceId?: ModelInstance['id'];
  onSave: (
    inputs: ISelectedInputs,
    disableNotification?: boolean,
    uploadedTemplateOverrides?: IUploadedTemplateOverrides,
    rowFilterPaths?: string[]
  ) => void;
  selectedInputs: ISelectedInputs;
}

interface State {
  category: string;
  selectedInputsValue: ISelectedInputs;
  filterInputsValue: string;
  selectedCategory: RowIdentifier['InputCategory'];
  hasVerticalScrollbar: boolean;
  showRemoveInputsModal: boolean;
  activeStep: number;
  isTemplateDataLoading: boolean;
  isTemplateUploading: boolean;
  templateFilterSelection: Record<string, boolean>;
  webAssumptionsLoadResponse: IInputsResponse[];
  uploadedTemplateFile?: File;
  uploadedTemplateOverrides?: IUploadedTemplateOverrides;
}

const SelectInputsModal: FunctionComponent<ISelectInputNamesModalProps> = ({
  modelInstance,
  scenario,
  isOpen,
  onClose,
  modelInstanceId,
  onSave,
  selectedInputs,
}) => {
  const location = useLocation();
  const {
    clientId,
    modelId,
    modelInstanceId: urlInstanceId,
    scenarioId,
  } = GetParams(location.search);
  const currentScope: ITemplateScope = {
    client: clientId,
    model: modelId,
    instance: urlInstanceId,
    scenario: scenarioId,
  };

  const { data } = useData<{
    rowIdentifiers?: RowIdentifier[];
    model?: Model;
  }>(
    () => ({
      rowIdentifiers:
        modelInstanceId !== undefined
          ? `/instances/${modelInstanceId}/row_identifiers`
          : undefined,
      model:
        clientId !== undefined && modelId !== undefined
          ? `/clients/${clientId}/models/${modelId}`
          : undefined,
    }),
    []
  );

  // Downloaded file name: ModelName-ModelInstanceName-ScenarioName
  const templateFileName = join(
    compact([data.model?.Name, modelInstance?.Name, scenario?.Name]),
    '-'
  );

  const inputs = orderBy(
    filter(data.rowIdentifiers, {
      Type: 'Input',
      DisableInputEdit: false,
    }),
    ['InputCategory'],
    ['asc']
  );

  const inputCategories = useMemo(
    () => uniq(map(inputs, 'InputCategory')),
    [inputs]
  );

  const [state, setState] = useSetState<State>({
    category: '',
    selectedInputsValue: selectedInputs,
    filterInputsValue: '',
    selectedCategory: inputCategories[0],
    hasVerticalScrollbar: false,
    showRemoveInputsModal: false,
    activeStep: 0,
    isTemplateDataLoading: false,
    isTemplateUploading: false,
    templateFilterSelection: {},
    webAssumptionsLoadResponse: [],
    uploadedTemplateFile: undefined,
    uploadedTemplateOverrides: undefined,
  });

  const templateFileInputRef = useRef<HTMLInputElement>(null);

  const filteredInputs = useMemo(
    () =>
      filter(inputs, (i) =>
        includes(toLower(i.Name), toLower(state.filterInputsValue))
      ),
    [inputs, state.filterInputsValue]
  );

  const inputCategoriesGrouped = groupBy(inputs, 'InputCategory');

  const filteredInputsGrouped = uniq(map(filteredInputs, 'InputCategory'));

  const sectionRefs = map(inputCategoriesGrouped, (_) =>
    React.createRef<HTMLUListElement>()
  );

  const onSelectInputs = (input: RowIdentifier): void => {
    setState((prevState) => {
      const isInputAlreadySelected = prevState.selectedInputsValue[input.Name];
      const currentDate = new Date();
      const dateString = currentDate.toISOString();
      return {
        ...prevState,
        selectedInputsValue: isInputAlreadySelected
          ? omit(prevState.selectedInputsValue, input.Name)
          : {
              ...prevState.selectedInputsValue,
              [input.Name]: { ...input, addedAt: dateString },
            },
      };
    });
  };

  const closeDialog = (): void => {
    setState({
      category: '',
      selectedInputsValue: {},
      filterInputsValue: '',
      uploadedTemplateFile: undefined,
      uploadedTemplateOverrides: undefined,
      activeStep: 0,
      templateFilterSelection: {},
    });
    onClose();
  };

  const handleFilterChange = (e: string): void => {
    setState({
      filterInputsValue: e,
    });
  };

  const handleChangeCategory = (
    category: RowIdentifier['InputCategory']
  ): void => {
    setState({
      selectedCategory: category,
    });
  };

  useEffect(() => {
    const categoryItem = document.getElementById(
      camelCase(state.selectedCategory as string)
    );
    if (!categoryItem) {
      return;
    }

    categoryItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [state.selectedCategory]);

  const flattenedSelectedInputs: RowIdentifier[] = useMemo(
    () => values(state.selectedInputsValue),
    [state.selectedInputsValue]
  );

  const unSelectedInputs = useMemo(
    () =>
      flattenedSelectedInputs.length === 10
        ? reject(inputs, (i) =>
            includes(map(flattenedSelectedInputs, 'id'), i.id)
          )
        : [],
    [flattenedSelectedInputs]
  );

  useLayoutEffect(() => {
    if (isOpen && !isEmpty(inputs)) {
      const div = document.getElementById('select-input-modal-root');
      if (div instanceof HTMLElement) {
        const hasVerticalScrollbar = div.scrollHeight > div.clientHeight;
        if (hasVerticalScrollbar && !state.hasVerticalScrollbar) {
          setState({ hasVerticalScrollbar });
        }
      }
    }
  }, [isOpen, inputs]);

  const clearAllInputs = useCallback(() => {
    setState({
      selectedInputsValue: {},
    });
  }, []);

  const selectedInputCategories = useMemo(() => {
    return groupBy(values(state.selectedInputsValue), 'InputCategory');
  }, [state.selectedInputsValue]);

  const renderPanel = (currentElementIndexInViewport?: number) => {
    return (
      <>
        <Grid md={3}>
          <Typography variant="h6">Input Categories</Typography>
          <List
            sx={{ border: `1px solid rgba(0, 0, 0, 0.12)`, my: 2 }}
            dense
            disablePadding
            data-cy="nav-wrapper"
          >
            {map(keys(inputCategoriesGrouped), (category, index) => (
              <Fragment key={category}>
                <ListItem
                  data-cy={`nav-item`}
                  disablePadding
                  secondaryAction={
                    <Tooltip
                      arrow
                      title={
                        size(selectedInputCategories[category]) > 0 && (
                          <ol>
                            {map(selectedInputCategories[category], (i) => (
                              <li key={i.id}>{i.Name}</li>
                            ))}
                          </ol>
                        )
                      }
                    >
                      <Typography
                        sx={{
                          color: (theme: Theme) => {
                            if (size(selectedInputCategories[category]) > 0) {
                              return 'black';
                            }
                            if (!includes(filteredInputsGrouped, category)) {
                              return '#D6D6D6';
                            }
                            return theme.palette.text.secondary;
                          },
                          fontWeight: () => {
                            if (size(selectedInputCategories[category]) > 0) {
                              return 600;
                            }
                          },
                        }}
                      >
                        {size(selectedInputCategories[category])}/
                        {inputCategoriesGrouped[category].length}
                      </Typography>
                    </Tooltip>
                  }
                >
                  {!isUndefined(currentElementIndexInViewport) ? (
                    <ListItemButton
                      disabled={!includes(filteredInputsGrouped, category)}
                      selected={
                        isEmpty(filteredInputsGrouped)
                          ? false
                          : currentElementIndexInViewport === index
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleChangeCategory(category);
                      }}
                      divider
                    >
                      <ListItemText primary={category} />
                    </ListItemButton>
                  ) : (
                    <ListItemButton
                      disabled={!includes(filteredInputsGrouped, category)}
                      selected={
                        (state.selectedCategory || inputCategories[0]) ===
                        category
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleChangeCategory(category);
                      }}
                      divider
                    >
                      <ListItemText primary={category} />
                    </ListItemButton>
                  )}
                </ListItem>
              </Fragment>
            ))}
          </List>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid md={8}>
          <Box sx={{ width: '100%' }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid>
                <Typography variant="h6">Inputs</Typography>
              </Grid>
              <Grid>
                <Button variant="text" onClick={clearAllInputs}>
                  Clear All
                </Button>
              </Grid>
            </Grid>
            <List
              sx={{
                width: '100%',
                bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'auto',
                maxHeight: 400,
                '& ul': { padding: 0 },
                my: 2,
              }}
              disablePadding
              subheader={<li />}
              data-cy="section-wrapper"
              id="select-input-modal-root"
            >
              {map(keys(inputCategoriesGrouped), (category, categoryIndex) => (
                <Fragment key={category}>
                  <Box component="li" id={camelCase(category)}>
                    {!isEmpty(
                      filter(inputCategoriesGrouped[category], (i) =>
                        includes(
                          toLower(i.Name),
                          toLower(state.filterInputsValue)
                        )
                      )
                    ) ? (
                      <>
                        <Box
                          component="ul"
                          ref={sectionRefs[categoryIndex]}
                          mb={2}
                        >
                          <Typography
                            component={ListSubheader}
                            variant="subtitle1"
                            disableGutters
                          >
                            <Grid
                              container
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Grid>{category}</Grid>
                            </Grid>
                          </Typography>
                          {map(
                            filter(inputCategoriesGrouped[category], (i) =>
                              includes(
                                toLower(i.Name),
                                toLower(state.filterInputsValue)
                              )
                            ),
                            (input) => (
                              <ListItem key={input.id} disableGutters>
                                <Chip
                                  label={input.Name}
                                  onClick={() => onSelectInputs(input)}
                                  variant={
                                    state.selectedInputsValue[input.Name]
                                      ? 'filled'
                                      : 'outlined'
                                  }
                                  disabled={includes(
                                    map(unSelectedInputs, 'id'),
                                    input.id
                                  )}
                                  sx={{
                                    color: (t) =>
                                      state.selectedInputsValue[input.Name]
                                        ? t.palette.secondary.main
                                        : t.palette.text.secondary,
                                    border: (t) =>
                                      state.selectedInputsValue[input.Name]
                                        ? `1px solid ${t.palette.secondary.main}`
                                        : `1px solid ${t.palette.divider}`,
                                    backgroundColor: (t) =>
                                      state.selectedInputsValue[input.Name]
                                        ? grey[100]
                                        : t.palette.background.paper,
                                  }}
                                />
                              </ListItem>
                            )
                          )}
                        </Box>
                        <Divider />
                      </>
                    ) : null}
                  </Box>
                </Fragment>
              ))}
            </List>
          </Box>
        </Grid>
      </>
    );
  };

  const loadData = async (
    inputs: ISelectedInputs,
    disableNotification?: boolean
  ): Promise<IInputsResponse[] | undefined> => {
    const webAssumptionsLoadPostData = await API.create<IWebAssumptionsLoad>(
      WEB_ASSUMPTIONS_POST(modelInstance?.id as number, scenario?.id as number),
      {
        Row_Identifier_Ids: map(
          values(!disableNotification ? inputs : difference),
          'id'
        ),
      }
    );

    const webAssumptionsLoad = await getDataAtIntervals(
      WEB_ASSUMPTIONS_LOAD(
        modelInstance?.id as number,
        scenario?.id as number,
        webAssumptionsLoadPostData?.id as number
      ),
      'Status'
    );

    if (webAssumptionsLoad?.Status === SubmissionStatus.Failed) {
      enqueueSnackbar(webAssumptionsLoad?.Status_Message, {
        variant: 'error',
      });
    }

    if (webAssumptionsLoad?.Status === SubmissionStatus.Finished) {
      const webAssumptionsLoadResponse: IInputsResponse[] = map(
        webAssumptionsLoad?.data.Inputs,
        (i) => {
          return {
            ...i,
            id: inputs[i.Name].id,
            FormatKey: find(formatParameters, {
              formatKey: inputs[i.Name].FormatKey,
            }) as IFormatParameters,
            TabName: inputs[i.Name].TabName,
            InputCategory: inputs[i.Name].InputCategory,
          };
        }
      );

      // Load Selection still renders the grid from state; the template download uses the return value
      setState({
        webAssumptionsLoadResponse: webAssumptionsLoadResponse,
      });
      return webAssumptionsLoadResponse;
    }
    return undefined;
  };

  // Step 1 -> Step 2: the report filters come straight from the Dimension / DimensionInstance row
  // identifiers, so no assumptions load is needed until the template is actually downloaded
  const goToFilterDimAndInstStep = (): void => {
    if (isEmpty(state.selectedInputsValue)) {
      return;
    }
    setState({
      activeStep: 1,
      templateFilterSelection: {},
    });
  };

  const goToSelectInputsStep = (): void => {
    setState({ activeStep: 0 });
  };

  // Only the first Report Dimension needs at least one instance selected; shows an error if it's empty
  const hasEmptyReportDimension = (): boolean => {
    const [emptyName] = getEmptyDimensionNames(
      data.rowIdentifiers,
      state.templateFilterSelection
    );
    if (emptyName === undefined) {
      return false;
    }
    enqueueSnackbar(
      `Report Dimension "${emptyName}" is empty. Please select at least 1 instance to continue.`,
      { variant: 'error' }
    );
    return true;
  };

  const confirmDownloadTemplate = async (): Promise<void> => {
    if (hasEmptyReportDimension()) {
      return;
    }
    setState({ isTemplateDataLoading: true });
    try {
      // Same data Load Selection renders in the AG Grid, fetched only now that the filters are final
      const loadedInputs = await loadData(state.selectedInputsValue);
      if (isEmpty(loadedInputs)) {
        // A failed load already shows its own error from loadData
        if (loadedInputs !== undefined) {
          enqueueSnackbar('No data was returned for the selected inputs.', {
            variant: 'warning',
          });
        }
        return;
      }
      const filteredInputs = filterInputsBySelection(
        loadedInputs as IInputsResponse[],
        getCheckedNames(state.templateFilterSelection)
      );
      await exportInputsAsExcel(
        filteredInputs,
        currentScope,
        templateFileName || undefined
      );
      setState({ activeStep: 0 });
    } catch (err) {
      enqueueSnackbar('Could not download the template. Please try again.', {
        variant: 'error',
      });
    } finally {
      setState({ isTemplateDataLoading: false });
    }
  };

  const uploadTemplate = (): void => {
    templateFileInputRef.current?.click();
  };

  // The Download template step's filters, or undefined when nothing is filtered out (every value
  // still checked) so Load Selection behaves exactly as it does without the step
  const getRowFilterPaths = (): string[] | undefined => {
    const selection = state.templateFilterSelection;
    if (
      state.activeStep !== 1 ||
      isEmpty(selection) ||
      every(values(selection))
    ) {
      return undefined;
    }
    return getCheckedNames(selection);
  };

  // Shared by the Load Selection button and the upload-template file picker: same Load Selection
  // pipeline either way, the only difference is whether uploaded values get overlaid on the result.
  const triggerLoadSelection = (
    uploadedTemplateOverrides?: IUploadedTemplateOverrides
  ): void => {
    const selectedInputsSize = size(selectedInputs);
    const selectedInputsValueSize = size(state.selectedInputsValue);
    const rowFilterPaths = getRowFilterPaths();
    const save = () =>
      onSave(
        state.selectedInputsValue,
        undefined,
        uploadedTemplateOverrides,
        rowFilterPaths
      );
    switch (true) {
      case isEmpty(state.selectedInputsValue) && isEmpty(selectedInputs):
        return;
      case isEmpty(selectedInputs) && !isEmpty(state.selectedInputsValue):
        save();
        break;
      case isEqual(selectedInputs, state.selectedInputsValue):
        if (uploadedTemplateOverrides || rowFilterPaths) {
          save();
        } else {
          closeDialog();
        }
        break;
      case every(selectedInputs, (value, key) =>
        has(state.selectedInputsValue, key)
      ) && selectedInputsValueSize > selectedInputsSize:
        save();
        break;
      case !isEmpty(selectedInputs) &&
        !isEqual(selectedInputs, state.selectedInputsValue):
        setState({
          showRemoveInputsModal: true,
        });
        break;
    }
  };

  const handleTemplateFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }
    // Reading a large workbook takes a few seconds before the load starts
    setState({ isTemplateUploading: true });
    try {
      const uploadedScope = await parseTemplateScope(file);
      if (!templateScopeMatches(uploadedScope, currentScope)) {
        enqueueSnackbar(
          `"${file.name}" was downloaded for a different client, model, instance or scenario (or has no scope information). Download a new template from this scenario and try again.`,
          { variant: 'error' }
        );
        return;
      }
      const uploadedTemplateOverrides = await parseTemplateWorkbook(file);
      // The file dictates which inputs to load - whatever is checked in the tree is irrelevant,
      // every sheet in the workbook maps to an input (by name) and becomes a tab.
      const sheetNames = keys(uploadedTemplateOverrides);
      const matchedInputs = filter(inputs, (input) =>
        includes(sheetNames, sheetNameFor(input.Name))
      );

      if (isEmpty(matchedInputs)) {
        enqueueSnackbar(
          `"${file.name}" doesn't contain any sheet matching an available input. Make sure it was exported from Download template.`,
          { variant: 'error' }
        );
        return;
      }

      // Same 10-input cap the checkbox tree enforces. Inputs already selected keep their spot;
      // new ones from the file fill whatever room is left, in the order they appear in the file.
      const MAX_SELECTED_INPUTS = 10;
      const alreadySelectedCount = size(state.selectedInputsValue);
      const newMatchedInputs = reject(matchedInputs, (input) =>
        has(state.selectedInputsValue, input.Name)
      );
      const availableSlots = Math.max(
        MAX_SELECTED_INPUTS - alreadySelectedCount,
        0
      );
      const inputsToAdd = newMatchedInputs.slice(0, availableSlots);
      const skippedInputs = newMatchedInputs.slice(availableSlots);

      const addedAt = new Date().toISOString();
      const selectedInputsFromFile = reduce(
        inputsToAdd,
        (acc, input) => ({
          ...acc,
          [input.Name]: { ...input, addedAt },
        }),
        {} as ISelectedInputs
      );
      const mergedSelection: ISelectedInputs = {
        ...state.selectedInputsValue,
        ...selectedInputsFromFile,
      };

      if (!isEmpty(skippedInputs)) {
        enqueueSnackbar(
          `Max ${MAX_SELECTED_INPUTS} inputs selected - not loading: ${map(skippedInputs, 'Name').join(', ')}`,
          { variant: 'warning' }
        );
      }

      setState({
        uploadedTemplateFile: file,
        uploadedTemplateOverrides,
        selectedInputsValue: mergedSelection,
      });
      enqueueSnackbar(`"${file.name}" uploaded. Applying to the grid...`, {
        variant: 'info',
      });
      onSave(mergedSelection, undefined, uploadedTemplateOverrides);
    } catch (err) {
      enqueueSnackbar(
        'Could not read the uploaded template. Make sure it is a valid .xlsx file.',
        { variant: 'error' }
      );
    } finally {
      setState({ isTemplateUploading: false });
    }
  };

  const onLoadInputs = async () => {
    if (hasEmptyReportDimension()) {
      return;
    }
    triggerLoadSelection(state.uploadedTemplateOverrides);
  };

  return (
    <>
      <input
        type="file"
        accept=".xlsx"
        ref={templateFileInputRef}
        style={{ display: 'none' }}
        onChange={handleTemplateFileSelected}
      />
      <RemoveInputNamesDialog
        open={state.showRemoveInputsModal}
        onClose={() =>
          setState({
            showRemoveInputsModal: false,
          })
        }
        onRemove={() =>
          onSave(
            state.selectedInputsValue,
            undefined,
            state.uploadedTemplateOverrides,
            getRowFilterPaths()
          )
        }
      />
      <PopupDialog
        scroll="paper"
        disableScrollLock
        open={isOpen}
        close={closeDialog}
        title="Select Inputs"
        maxWidth="lg"
        primaryButtons={
          state.activeStep === 0
            ? [
                {
                  id: 'nextStepButton',
                  label: 'Next',
                  onClick: goToFilterDimAndInstStep,
                  disabled: flattenedSelectedInputs.length === 0,
                },
                {
                  id: 'uploadTemplateButton',
                  label: 'Upload template',
                  onClick: uploadTemplate,
                  disabled: isEmpty(inputs),
                  align: 'left',
                },
              ]
            : [
                {
                  id: 'backToSelectInputsButton',
                  label: 'Back',
                  onClick: goToSelectInputsStep,
                  variant: 'outlined',
                  align: 'left',
                  loading: false,
                  disabled: state.isTemplateDataLoading,
                },
                {
                  id: 'loadSelectionButton',
                  label: 'Load Selection',
                  onClick: onLoadInputs,
                  loading: false,
                  disabled:
                    flattenedSelectedInputs.length === 0 ||
                    state.isTemplateDataLoading,
                },
                {
                  id: 'successDownloadTemplateButton',
                  label: 'Download Template',
                  onClick: confirmDownloadTemplate,
                  loading: state.isTemplateDataLoading,
                  disabled: isEmpty(data.rowIdentifiers),
                },
              ]
        }
      >
        <Stepper activeStep={state.activeStep} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Select Inputs</StepLabel>
          </Step>
          <Step>
            <StepLabel>Filter Dimension and Instances</StepLabel>
          </Step>
        </Stepper>
        {state.activeStep === 1 && state.isTemplateDataLoading && (
          <Box sx={{ mb: 2 }}>
            <CenteredSpinner />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              Preparing your template, please wait...
            </Typography>
          </Box>
        )}
        {state.isTemplateUploading ? (
          <Box sx={{ mb: 2 }}>
            <CenteredSpinner />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              Reading your uploaded file, please wait...
            </Typography>
          </Box>
        ) : state.activeStep === 1 ? (
          // Hidden rather than unmounted while loading, so its select-all effect doesn't re-run
          // and wipe the user's choices if the download fails or comes back empty
          <Box sx={{ display: state.isTemplateDataLoading ? 'none' : 'block' }}>
            <FilterDimAndInstStep
              rowIdentifiers={data.rowIdentifiers}
              selectedNames={state.templateFilterSelection}
              onChange={(templateFilterSelection) =>
                setState({ templateFilterSelection })
              }
            />
          </Box>
        ) : !isEmpty(inputs) ? (
          <>
            {state.uploadedTemplateFile && (
              <Alert
                sx={{ mb: 2 }}
                severity="info"
                onClose={() =>
                  setState({
                    uploadedTemplateFile: undefined,
                    uploadedTemplateOverrides: undefined,
                  })
                }
              >
                Template "{state.uploadedTemplateFile.name}" applied to the
                grid.
              </Alert>
            )}
            {flattenedSelectedInputs.length >= 10 && (
              <Alert sx={{ mb: 2 }} severity="error">
                Max number of inputs selected
              </Alert>
            )}
            <Grid container alignItems="center">
              <Grid md={4} />
              <Grid md={3}>
                <ClearableTextField
                  id="filterInputNames"
                  label="Filter Inputs"
                  InputProps={{
                    startAdornment: <Search />,
                  }}
                  size="small"
                  value={state.filterInputsValue}
                  onHandleChange={handleFilterChange}
                  fullWidth
                />
              </Grid>
              <Grid md={5} container justifyContent="flex-end">
                <Typography>
                  Total Selected :{' '}
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>
                    {flattenedSelectedInputs.length}/10
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
            {isEmpty(filteredInputsGrouped) ? (
              <Alert severity="error" sx={{ my: 2 }}>
                <AlertTitle>
                  There are no inputs pertaining to the filter you entered.
                </AlertTitle>
              </Alert>
            ) : (
              <Grid container mt={2} justifyContent="space-between">
                {state.hasVerticalScrollbar ? (
                  <Scrollspy
                    sectionRefs={sectionRefs}
                    rootSelector="#select-input-modal-root"
                  >
                    {({ currentElementIndexInViewport }) =>
                      renderPanel(currentElementIndexInViewport)
                    }
                  </Scrollspy>
                ) : (
                  renderPanel()
                )}
              </Grid>
            )}
          </>
        ) : (
          <CenteredSpinner />
        )}
      </PopupDialog>
    </>
  );
};

export default SelectInputsModal;
